import authService from './authService';
import { ensureOk, readData, readList, readPaginated, toQuery } from './apiResponse';
import { buildMediaQuery } from '@/utils/mediaFilters';
import type { PaginatedList } from '@/types/activity';
import type {
  DuplicateAction,
  ResolveDuplicateResult,
  DuplicateCandidate,
  DuplicateListStatus,
  DuplicateMatchKind,
  MediaAsset,
  MediaBulkFlagsRequest,
  MediaBulkRequest,
  MediaBulkResult,
  MediaCompression,
  MediaCompressionAcceptResult,
  MediaCompressionMeta,
  MediaCompressionRequest,
  MediaEvent,
  MediaEventFormData,
  MediaFilter,
  MediaFlagsRequest,
  MediaImportItem,
  MediaImportItemStatus,
  MediaImportSource,
  MediaImportSourceFormData,
  MediaReprocessStep,
  MediaSignedUrl,
  MediaStats,
  MediaTag,
  MediaTagAssignment,
  MediaTagCategory,
  MediaTagCategoryFormData,
  MediaTagCreateRequest,
  MediaTagUpdateRequest,
  MediaUpdateRequest,
  MediaUploadRequest,
  MediaUploadTicket,
  MediaUrlVariant,
  ProductSearchResult,
} from '../types/media';

/** Normaliza coleções que o backend pode mandar como `null`. */
const normalize = (m: MediaAsset): MediaAsset => ({ ...m, tags: m?.tags ?? [], products: m?.products ?? [] });

/**
 * Biblioteca de mídias — ver docs/API.md do marketingbackgo, seção "Mídias". O arquivo em si
 * nunca passa pela API: o envio vai direto ao Blob pela URL SAS (ver mediaUpload.ts).
 */
class MediaService {
  private async getApiClient() {
    return authService.getApiClient();
  }

  // ------------------------------------------------------------ upload

  /** POST /media/uploads → URL SAS de escrita */
  async createUpload(request: MediaUploadRequest): Promise<MediaUploadTicket> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/media/uploads', request);
    return readData<MediaUploadTicket>(response, 'Falha ao iniciar o envio do arquivo');
  }

  /** POST /media/:id/complete → MediaAsset (status pending) */
  async complete(id: number): Promise<MediaAsset> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/media/${id}/complete`);
    return normalize(await readData<MediaAsset>(response, 'Falha ao confirmar o envio do arquivo'));
  }

  // ------------------------------------------------------------ consulta

  /** GET /media (paginado) */
  async list(filter: MediaFilter): Promise<PaginatedList<MediaAsset>> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/media${buildMediaQuery(filter)}`);
    const res = await readPaginated<MediaAsset>(response, 'Falha ao buscar as mídias');
    return { ...res, data: res.data.map(normalize) };
  }

  /** GET /media/stats */
  async stats(): Promise<MediaStats> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/media/stats');
    return readData<MediaStats>(response, 'Falha ao buscar os totais da biblioteca');
  }

  /** GET /media/:id (com renditions e preview_url) */
  async getById(id: number): Promise<MediaAsset> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/media/${id}`);
    return normalize(await readData<MediaAsset>(response, 'Falha ao buscar a mídia'));
  }

  /** GET /media/:id/url?variant=&download=1 → SAS de leitura (~15 min) */
  async getUrl(id: number, variant: MediaUrlVariant, download = false): Promise<MediaSignedUrl> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/media/${id}/url${toQuery({ variant, download: download ? 1 : undefined })}`);
    return readData<MediaSignedUrl>(response, 'Falha ao gerar o link do arquivo');
  }

  // ------------------------------------------------------------ edição

  /** PUT /media/:id */
  async update(id: number, data: MediaUpdateRequest): Promise<MediaAsset> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/media/${id}`, data);
    return normalize(await readData<MediaAsset>(response, 'Falha ao salvar a mídia'));
  }

  /** PATCH /media/:id/flags */
  async setFlags(id: number, flags: MediaFlagsRequest): Promise<MediaAsset | null> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.patch(`/media/${id}/flags`, flags);
    const data = await readData<MediaAsset | null>(response, 'Falha ao alterar a mídia');
    return data && typeof data === 'object' && 'id' in data ? normalize(data) : null;
  }

  /** PUT /media/:id/tags — substitui o conjunto */
  async setTags(id: number, tags: MediaTagAssignment[]): Promise<MediaAsset | null> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/media/${id}/tags`, { tags });
    const data = await readData<MediaAsset | null>(response, 'Falha ao salvar as tags');
    return data && typeof data === 'object' && 'id' in data ? normalize(data) : null;
  }

  /** PUT /media/:id/products — substitui o conjunto */
  async setProducts(id: number, skus: string[]): Promise<MediaAsset | null> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/media/${id}/products`, { products: skus.map((sku) => ({ sku })) });
    const data = await readData<MediaAsset | null>(response, 'Falha ao salvar os produtos');
    return data && typeof data === 'object' && 'id' in data ? normalize(data) : null;
  }

  /** POST /media/bulk */
  async bulk(request: MediaBulkRequest): Promise<MediaBulkResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/media/bulk', request);
    const data = await readData<MediaBulkResult>(response, 'Falha ao alterar as mídias selecionadas');
    return { updated: data?.updated ?? 0 };
  }

  /** POST /media/bulk-flags */
  async bulkFlags(request: MediaBulkFlagsRequest): Promise<MediaBulkResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/media/bulk-flags', request);
    const data = await readData<MediaBulkResult>(response, 'Falha ao alterar as mídias selecionadas');
    return { updated: data?.updated ?? 0 };
  }

  /** POST /media/:id/reprocess (steps vazio = todos) */
  async reprocess(id: number, steps?: MediaReprocessStep[]): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/media/${id}/reprocess`, { steps: steps ?? [] });
    await ensureOk(response, 'Falha ao reprocessar a mídia');
  }

  // ------------------------------------------------------------ compactar imagem

  /** GET /media/compression-meta — opções da tela e se o conversor está configurado */
  async compressionMeta(): Promise<MediaCompressionMeta> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/media/compression-meta');
    const data = await readData<MediaCompressionMeta>(response, 'Falha ao carregar as opções de compactação');
    return { ...data, presets: data?.presets ?? [] };
  }

  /** POST /media/:id/compress → 202 MediaCompression (pending) */
  async compress(id: number, request: MediaCompressionRequest): Promise<MediaCompression> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/media/${id}/compress`, request);
    return readData<MediaCompression>(response, 'Falha ao pedir a compactação');
  }

  /** GET /media/:id/compressions/open — a compactação aberta (ou null) */
  async openCompression(id: number): Promise<MediaCompression | null> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/media/${id}/compressions/open`);
    const data = await readData<MediaCompression | null>(response, 'Falha ao consultar a compactação');
    return data && typeof data === 'object' && 'id' in data ? data : null;
  }

  /** GET /media/:id/compressions/:cid */
  async compression(id: number, compressionId: number): Promise<MediaCompression> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/media/${id}/compressions/${compressionId}`);
    return readData<MediaCompression>(response, 'Falha ao consultar a compactação');
  }

  /** POST /media/:id/compressions/:cid/accept — "Usar a nova" */
  async acceptCompression(id: number, compressionId: number): Promise<MediaCompressionAcceptResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/media/${id}/compressions/${compressionId}/accept`, {});
    const data = await readData<MediaCompressionAcceptResult>(response, 'Falha ao usar a imagem compactada');
    return { ...data, media: normalize(data.media), warnings: data?.warnings ?? [] };
  }

  /** POST /media/:id/compressions/:cid/discard — "Manter a original" */
  async discardCompression(id: number, compressionId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/media/${id}/compressions/${compressionId}/discard`, {});
    await ensureOk(response, 'Falha ao descartar a imagem compactada');
  }

  /** DELETE /media/:id (exclusão lógica) */
  async remove(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/media/${id}`);
    await ensureOk(response, 'Falha ao excluir a mídia');
  }

  /** GET /media/products/search?q= (até 20) */
  async searchProducts(q: string): Promise<ProductSearchResult[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(`/media/products/search${toQuery({ q: q.trim() })}`);
    return readList<ProductSearchResult>(response, 'Falha ao buscar produtos');
  }

  // ------------------------------------------------------------ duplicadas

  /** GET /media/duplicates?status=&match_kind=&page= */
  async duplicates(params: {
    status: DuplicateListStatus;
    matchKind?: DuplicateMatchKind | '';
    page: number;
    pageSize?: number;
  }): Promise<PaginatedList<DuplicateCandidate>> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/media/duplicates${toQuery({
        status: params.status,
        match_kind: params.matchKind || undefined,
        page: params.page,
        page_size: params.pageSize,
      })}`,
    );
    const res = await readPaginated<DuplicateCandidate>(response, 'Falha ao buscar as duplicadas');
    return {
      ...res,
      data: res.data.map((d) => ({ ...d, media_a: normalize(d.media_a), media_b: normalize(d.media_b) })),
    };
  }

  /** POST /media/duplicates/:id/resolve */
  async resolveDuplicate(id: number, action: DuplicateAction): Promise<ResolveDuplicateResult> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/media/duplicates/${id}/resolve`, { action });
    const data = await readData<ResolveDuplicateResult | null>(response, 'Falha ao resolver a duplicada');
    return data ?? { landing_pages_updated: 0 };
  }

  // ------------------------------------------------------------ taxonomia

  /** GET /media/tag-categories */
  async tagCategories(): Promise<MediaTagCategory[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/media/tag-categories');
    return readList<MediaTagCategory>(response, 'Falha ao buscar as categorias de tags');
  }

  /** POST /media/tag-categories */
  async createTagCategory(data: MediaTagCategoryFormData): Promise<MediaTagCategory> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/media/tag-categories', data);
    return readData<MediaTagCategory>(response, 'Falha ao criar a categoria');
  }

  /** PUT /media/tag-categories/:id */
  async updateTagCategory(id: number, data: MediaTagCategoryFormData): Promise<MediaTagCategory> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/media/tag-categories/${id}`, data);
    return readData<MediaTagCategory>(response, 'Falha ao salvar a categoria');
  }

  /** GET /media/tags?category_id=&search=&include_inactive= */
  async tags(params: { categoryId?: number; search?: string; includeInactive?: boolean } = {}): Promise<MediaTag[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/media/tags${toQuery({
        category_id: params.categoryId,
        search: params.search?.trim(),
        include_inactive: params.includeInactive,
      })}`,
    );
    const list = await readList<MediaTag>(response, 'Falha ao buscar as tags');
    return list.map((t) => ({ ...t, synonyms: t.synonyms ?? [] }));
  }

  /** POST /media/tags */
  async createTag(data: MediaTagCreateRequest): Promise<MediaTag> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/media/tags', data);
    return readData<MediaTag>(response, 'Falha ao criar a tag');
  }

  /** PUT /media/tags/:id */
  async updateTag(id: number, data: MediaTagUpdateRequest): Promise<MediaTag> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/media/tags/${id}`, data);
    return readData<MediaTag>(response, 'Falha ao salvar a tag');
  }

  /** POST /media/tags/:id/merge — move os vínculos para `intoTagId` e inativa a origem */
  async mergeTag(id: number, intoTagId: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/media/tags/${id}/merge`, { into_tag_id: intoTagId });
    await ensureOk(response, 'Falha ao mesclar as tags');
  }

  // ------------------------------------------------------------ eventos

  /** GET /media/events */
  async events(): Promise<MediaEvent[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/media/events');
    return readList<MediaEvent>(response, 'Falha ao buscar os eventos');
  }

  /** POST /media/events */
  async createEvent(data: MediaEventFormData): Promise<MediaEvent> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/media/events', data);
    return readData<MediaEvent>(response, 'Falha ao criar o evento');
  }

  /** PUT /media/events/:id */
  async updateEvent(id: number, data: MediaEventFormData): Promise<MediaEvent> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/media/events/${id}`, data);
    return readData<MediaEvent>(response, 'Falha ao salvar o evento');
  }

  /** DELETE /media/events/:id (inativa; as mídias ficam sem evento) */
  async deleteEvent(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.delete(`/media/events/${id}`);
    await ensureOk(response, 'Falha ao excluir o evento');
  }

  // ------------------------------------------------------------ importação OneDrive

  /** GET /media/import-sources */
  async importSources(): Promise<MediaImportSource[]> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get('/media/import-sources');
    return readList<MediaImportSource>(response, 'Falha ao buscar as fontes de importação');
  }

  /** POST /media/import-sources */
  async createImportSource(data: MediaImportSourceFormData): Promise<MediaImportSource> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post('/media/import-sources', data);
    return readData<MediaImportSource>(response, 'Falha ao criar a fonte de importação');
  }

  /** PUT /media/import-sources/:id */
  async updateImportSource(id: number, data: MediaImportSourceFormData): Promise<MediaImportSource> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.put(`/media/import-sources/${id}`, data);
    return readData<MediaImportSource>(response, 'Falha ao salvar a fonte de importação');
  }

  /** POST /media/import-sources/:id/run → 202 (o worker executa) */
  async runImportSource(id: number): Promise<void> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.post(`/media/import-sources/${id}/run`);
    await ensureOk(response, 'Falha ao iniciar a importação');
  }

  /** GET /media/import-items?source_id=&status=&page= */
  async importItems(params: {
    sourceId?: number;
    status?: MediaImportItemStatus | '';
    page: number;
    pageSize?: number;
  }): Promise<PaginatedList<MediaImportItem>> {
    const apiClient = await this.getApiClient();
    const response = await apiClient.get(
      `/media/import-items${toQuery({
        source_id: params.sourceId,
        status: params.status || undefined,
        page: params.page,
        page_size: params.pageSize,
      })}`,
    );
    return readPaginated<MediaImportItem>(response, 'Falha ao buscar os itens importados');
  }
}

export const mediaService = new MediaService();
export default mediaService;
